import { getActiveOfferTemplate, getOperation, postAction } from "../../api"
import { newNotification, openOverlay } from "../../store"
import { get } from "svelte/store"
import { _ } from "svelte-i18n"
import { isOperation, isOperationOut } from "./typeGuards"

export async function checkIfOrderedAndItsStatus(op: Operation | OperationOut, orderedOperationStatus: any) {
    if (op?.type == 2) {
        orderedOperationStatus.available = true
        if (isOperation(op) && op.statusSummary?.canceled == 100) return
        orderedOperationStatus.notCanceled = true
        orderedOperationStatus.withNotCanceledPositions = true
        return orderedOperationStatus
    }
    if (op) {
        const query = {
            type: 2,
            "positions.operationOutOfferReference.id": op.id,
        }
        let oper = await getOperation("operation", query, undefined, undefined, "referenceCheck")
        if (Array.isArray(oper.data) && oper?.data?.length) {
            const orderedOperation = oper.data[0] as Operation | OperationOut
            if (orderedOperation) {
                orderedOperationStatus.available = true
                if (isOperation(orderedOperation) && orderedOperation.statusSummary?.canceled == 100) {
                    orderedOperationStatus.notCanceled = false
                    orderedOperationStatus.withNotCanceledPositions = false
                    return orderedOperationStatus
                }
                orderedOperationStatus.notCanceled = true
                orderedOperation.positions = orderedOperation?.positions?.filter(
                    (p) => p?.operationOutOfferReference?.id == op.id
                )
                let notCanceledPositions = orderedOperation.positions.filter((p) => p?.status.blockingIndicator != 2)
                if (notCanceledPositions.length) orderedOperationStatus.withNotCanceledPositions = true
                else orderedOperationStatus.withNotCanceledPositions = false
                return orderedOperationStatus
            }
        }
    }
}

export async function checkIfTemplateOnOpOrOnClerkIsAvailable(
    op: OperationOut | Operation,
    dealerNumberOfClerk: number
) {
    const type = op.type === 1 ? "offer" : "orderConfirmation"

    // Helper function to check for template availability
    async function checkTemplate(dealerNumber: number, templateType: string, templateSource: number | null) {
        const response = await getActiveOfferTemplate(dealerNumber, templateType, templateSource)
        const template = response?.data?.[0]
        return Boolean(template)
    }

    // Check for various combinations of dealerNumber and source
    if (
        (await checkTemplate(op.dealerNumber, type, op.source)) ||
        (await checkTemplate(op.dealerNumber, type, null)) ||
        (await checkTemplate(dealerNumberOfClerk, type, op.source)) ||
        (await checkTemplate(dealerNumberOfClerk, type, null))
    ) {
        return true
    }

    return false
}

function isOperationStatusValid(orderedOperationStatus: any) {
    return (
        orderedOperationStatus.available &&
        orderedOperationStatus.notCanceled &&
        orderedOperationStatus.withNotCanceledPositions
    )
}

export function getSendOfferConfirmationTooltip(
    offerChanged: boolean,
    orderedOperationStatus: any,
    op: Operation | OperationOut
) {
    return offerChanged
        ? get(_)("components.operationDetails.offerChanged")
        : !orderedOperationStatus.available
          ? get(_)("components.operationDetails.noCorrespondingOrder")
          : !orderedOperationStatus.notCanceled
            ? get(_)("components.operationDetails.orderCanceled")
            : !orderedOperationStatus.withNotCanceledPositions
              ? get(_)("components.operationDetails.allPositionsCanceled")
              : offerChanged
                ? get(_)("components.operationDetails.orderConfirmationChanged")
                : op?.statusSummary?.created !== 100
                  ? get(_)("components.operationDetails.orderConfirmationNotComplete")
                  : "invalid"
}

export function checkIfOfferConfirmationIsDisabled(
    orderedOperationStatus: any,
    offerChanged: boolean,
    op: Operation | OperationOut,
    loading: boolean,
    somePositionsSelected: boolean
) {
    return (
        !isOperationStatusValid(orderedOperationStatus) ||
        loading ||
        offerChanged ||
        !somePositionsSelected ||
        op?.statusSummary?.created !== 100
    )
}
export async function sendOfferConfirmation(op: OperationOut) {
    let error = false
    await postAction("createOrderConfirmationPDF", {
        id: op.id,
    }).catch((e) => {
        newNotification({
            class: "error",
            html: get(_)("components.operationDetails.orderConfirmationTemplateError") + e.message,
            timeout: 10000,
        })
        error = true
    })

    if (error) return
    openOverlay(op, "email", {
        operation: op,
        type: "sendOrderConfirmation",
    })
}

export function getDuplicateOperationTooltip(offerChanged: boolean, somePositionsSelected: boolean) {
    return offerChanged
        ? get(_)("components.operationDetails.offerChanged")
        : !somePositionsSelected
          ? get(_)("components.operationDetails.selectAtLeastOnePosition")
          : "invalid"
}

export function checkIfOperationDuplicationIsDisabled(
    loading: boolean,
    offerChanged: boolean,
    somePositionsSelected: boolean
) {
    return loading || offerChanged || !somePositionsSelected
}

export function getCreateOfferConfirmationTooltip(
    offerChanged: boolean,
    orderedOperationStatus: any,
    op: Operation | OperationOut,
    somePositionsSelected: boolean
) {
    return offerChanged
        ? get(_)("components.operationDetails.orderConfirmationChanged")
        : !orderedOperationStatus.available
          ? get(_)("components.operationDetails.noCorrespondingOrder")
          : !orderedOperationStatus.notCanceled
            ? get(_)("components.operationDetails.orderCanceled")
            : !orderedOperationStatus.withNotCanceledPositions
              ? get(_)("components.operationDetails.allPositionsCanceled")
              : !somePositionsSelected
                ? get(_)("components.operationDetails.selectAtLeastOnePosition")
                : op?.statusSummary?.created !== 100
                  ? get(_)("components.operationDetails.orderConfirmationNotComplete")
                  : get(_)("components.operationDetails.dataFromIncomingOrder")
}

export function checkIfOfferCreationIsDisabled(
    orderedOperationStatus: any,
    offerChanged: boolean,
    op: Operation | OperationOut,
    loading: boolean,
    somePositionsSelected: boolean
) {
    return (
        !isOperationStatusValid(orderedOperationStatus) ||
        loading ||
        offerChanged ||
        !somePositionsSelected ||
        op?.statusSummary?.created !== 100
    )
}
export function getSendOfferTooltip(offerChanged: boolean, op: Operation | OperationOut) {
    return offerChanged
        ? get(_)("components.operationDetails.offerChanged")
        : op?.statusSummary?.created !== 100
          ? get(_)("components.operationDetails.offerNotCompleted")
          : "invalid"
}
export function checkIfSendOfferIsDisabled(
    loading: boolean,
    offerChanged: boolean,
    op: Operation | OperationOut,
    somePositionsSelected: boolean
) {
    return loading || offerChanged || !somePositionsSelected || op?.statusSummary?.created !== 100
}
export function checkIfEditTemplateIsDisabled(
    loading: boolean,
    somePositionsSelected: boolean,
    op: Operation | OperationOut
) {
    return (
        loading ||
        !somePositionsSelected ||
        !!(isOperationOut(op) && op.statusSummary?.emailed) ||
        !!(isOperationOut(op) && op.statusSummary?.orderConfimationEmailed)
    )
}

export function getEditTemplateTooltip(op: Operation | OperationOut) {
    return isOperationOut(op) && op.statusSummary?.emailed
        ? get(_)("components.operationDetails.offerAlreadySent")
        : isOperationOut(op) && op.statusSummary?.orderConfimationEmailed
          ? get(_)("components.operationDetails.orderConfirmationAlreadySent")
          : "invalid"
}

export function checkIfSaveIsDisabled(
    op: Operation | OperationOut,
    loading: boolean,
    offerChanged: boolean,
    somePositionsSelected: boolean
) {
    return (
        !!(isOperationOut(op) && op.statusSummary?.emailed) ||
        !!(isOperationOut(op) && op.statusSummary?.orderConfimationEmailed) ||
        loading ||
        !offerChanged ||
        !somePositionsSelected
    )
}

export function getSaveTooltip(offerChanged: boolean, somePositionsSelected: boolean, op: Operation | OperationOut) {
    return isOperationOut(op) && op.statusSummary?.emailed
        ? get(_)("components.operationDetails.offerAlreadySent")
        : isOperationOut(op) && op.statusSummary?.orderConfimationEmailed
          ? get(_)("components.operationDetails.orderConfirmationAlreadySent")
          : offerChanged
            ? !somePositionsSelected
                ? get(_)("components.operationDetails.selectAtLeastOnePosition")
                : `${
                      op.type == 1
                          ? get(_)("components.operationDetails.offerChanged")
                          : get(_)("components.operationDetails.orderConfirmationChanged")
                  }`
            : `${
                  op.type == 1
                      ? get(_)("components.operationDetails.offerNotChanged")
                      : get(_)("components.operationDetails.orderConfirmationNotChanged")
              }`
}

export function getResetTooltip(offerChanged: boolean, op: Operation | OperationOut) {
    return offerChanged
        ? `${
              op.type == 1
                  ? get(_)("components.operationDetails.offerReset")
                  : get(_)("components.operationDetails.orderConfirmationReset")
          }`
        : `${
              op.type == 1
                  ? get(_)("components.operationDetails.offerNotChanged")
                  : get(_)("components.operationDetails.orderConfirmationNotChanged")
          }`
}

export function checkIfOrderConfirmationConvertionIsDisabled(
    loading: boolean,
    somePositionsSelected: boolean,
    op: Operation | OperationOut,
    templateExists: boolean
) {
    return (
        loading ||
        !somePositionsSelected ||
        (isOperation(op) && op.statusSummary?.canceled == 100) ||
        op?.statusSummary?.confirmed !== 100 ||
        !templateExists
    )
}

export function getOrderConfirmationConvertionTooltip(
    somePositionsSelected: boolean,
    op: Operation | OperationOut,
    templateExists: boolean
) {
    return !somePositionsSelected
        ? get(_)("components.operationDetails.selectAtLeastOnePosition")
        : op?.statusSummary?.confirmed !== 100
          ? get(_)("components.operationDetails.orderNotComplete")
          : isOperation(op) && op.statusSummary?.canceled == 100
            ? get(_)("components.operationDetails.orderCanceled")
            : !templateExists
              ? get(_)("components.operationDetails.templateNotAvailable")
              : op?.positions?.filter((p) => p?.operationOutOfferReference?.id).length
                ? get(_)("components.operationDetails.offerDataInherited")
                : "invalid"
}

export function checkIfOfferConvertionIsDisabled(
    loading: boolean,
    somePositionsSelected: boolean,
    templateExists: boolean
) {
    return loading || !somePositionsSelected || !templateExists
}

export function getOfferConvertionTooltip(somePositionsSelected: boolean, templateExists: boolean) {
    return !somePositionsSelected
        ? get(_)("components.operationDetails.selectAtLeastOnePosition")
        : !templateExists
          ? get(_)("components.operationDetails.templateNotAvailable")
          : "invalid"
}
